/**
 * SEOHead — Multi-Tenant Dynamic SEO Component
 *
 * Uses react-helmet-async to dynamically update all meta tags
 * based on tenant configuration fetched from the server.
 *
 * Usage:
 *   <SEOHead appType="client" route="/step/2" />
 *   <SEOHead appType="followup" />
 *
 * Tenant is detected automatically from:
 *   1. window.__TENANT_DATA__ (set by early detection script)
 *   2. API call to /api/seo/config/:appType?domain=hostname
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { environment } from '../env/environment';
import logger from '../utils/logger';

const SEO_CACHE = {}; // In-memory cache to avoid redundant API calls

const SEOHead = ({ appType = 'client', route = 'default', customTitle, customDescription }) => {
  const [seo, setSeo] = useState(null);

  useEffect(() => {
    const loadSeo = async () => {
      // Check cache first
      const cacheKey = `${appType}:${route}`;
      if (SEO_CACHE[cacheKey]) {
        setSeo(SEO_CACHE[cacheKey]);
        return;
      }

      // Try to build from pre-fetched tenant data (no extra API call)
      const tenantData = window.__TENANT_DATA__;
      if (tenantData) {
        const ab = (tenantData.account_branding || {})[appType] || {};
        const seoConfig = (tenantData.seo_config || {})[appType] || {};
        const routeSeo = seoConfig[route] || seoConfig['default'] || {};
        const brandName = tenantData.brand_name || tenantData.name || '';

        const seoObj = {
          title: routeSeo.title || ab.metaTitle || brandName,
          description: routeSeo.description || ab.metaDescription || '',
          keywords: routeSeo.keywords || '',
          ogTitle: routeSeo.ogTitle || routeSeo.title || ab.metaTitle || brandName,
          ogDescription: routeSeo.ogDescription || routeSeo.description || ab.metaDescription || '',
          ogImageUrl: routeSeo.ogImage && routeSeo.ogImage.startsWith('data:')
            ? `${environment.baseUrl}/api/seo/image/${appType}?tenant=${tenantData.slug}`
            : routeSeo.ogImage || null,
          ogUrl: window.location.href,
          twitterCard: routeSeo.twitterCard || 'summary_large_image',
          canonical: window.location.href,
          themeColor: routeSeo.themeColor || ab.primaryColor || tenantData.primary_color || '#088395',
        };

        SEO_CACHE[cacheKey] = seoObj;
        setSeo(seoObj);
        return;
      }

      // Fallback: API call
      try {
        const domain = window.location.hostname;
        const res = await fetch(`${environment.baseUrl}/api/seo/config/${appType}?domain=${domain}&route=${route}`);
        const data = await res.json();
        if (data.success && data.seo) {
          SEO_CACHE[cacheKey] = data.seo;
          setSeo(data.seo);
        }
      } catch (err) {
        logger.warn('[SEOHead] Failed to load SEO config:', err.message);
      }
    };

    loadSeo();
  }, [appType, route]);

  if (!seo) return null;

  const title = customTitle || seo.title;
  const description = customDescription || seo.description;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="theme-color" content={seo.themeColor} />
      <link rel="canonical" href={seo.canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.ogTitle || title} />
      <meta property="og:description" content={seo.ogDescription || description} />
      {seo.ogImageUrl && <meta property="og:image" content={seo.ogImageUrl} />}
      <meta property="og:url" content={seo.ogUrl} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={seo.twitterCard || 'summary_large_image'} />
      <meta name="twitter:title" content={seo.ogTitle || title} />
      <meta name="twitter:description" content={seo.ogDescription || description} />
      {seo.ogImageUrl && <meta name="twitter:image" content={seo.ogImageUrl} />}
    </Helmet>
  );
};

export default SEOHead;
